<template>
  <article>
    <div v-if="title" class="chart-title">
      {{ title }}
    </div>
    <line-chart
      :chart-data="chartData"
      :chart-options="chartOptions"
      style="min-height: 440px"
    />
  </article>
</template>

<script>
  import { Line as LineChart } from 'vue-chartjs'
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement,
    TimeScale,
  } from 'chart.js'

  import 'chartjs-adapter-date-fns'
  import { createTooltip } from '@/util/revenue-dynamics/tooltip'
  import { positioningTooltip } from '@/util/chart'
  import { createSVPBlock, getSVPColor } from '@/util/statistics'

  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    LineElement,
    LinearScale,
    CategoryScale,
    PointElement,
    TimeScale,
  )

  export default {
    name: 'OrdersChart',
    components: { LineChart },
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      timeUnit: {
        type: String,
        default: 'day',
      },
      title: {
        type: String,
        default: null,
      },
    },
    computed: {
      chartOptions () {
        return {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              type: 'time',
              time: {
                unit: this.timeUnit,
                tooltipFormat: 'yyyy-MM-dd',
                displayFormats: {
                  day: 'yyyy-MM-dd',
                  week: 'yyyy-MM-dd',
                  month: 'yyyy-MM',
                },
              },
              title: {
                display: true,
                text: this.datasetLabel,
                align: 'end',
                color: '#000',
                font: {
                  size: 14,
                },
              },
              grid: {
                display: true,
                drawOnChartArea: false,
              },
              ticks: {
                callback: (value) => {
                  const date = new Date(value)

                  return this.timeUnit === 'month'
                    ? date.toLocaleDateString('ru', {
                      month: 'short',
                      year: 'numeric',
                    })
                    : date.toLocaleDateString('ru', {
                      day: 'numeric',
                      month: 'short',
                    })
                },
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: this.externalTooltipHandler,
            },
          },
        }
      },
      chartData () {
        return {
          labels: this.items.map((item) => item.date),
          datasets: [
            {
              backgroundColor: '#FF9D00',
              borderColor: '#FF9D00',
              data: this.acceptedOrdersDatasetData,
              cubicInterpolationMode: 'monotone',
              pointRadius: 2,
              pointHoverRadius: 8,
            },
            {
              backgroundColor: '#EA306B',
              borderColor: '#EA306B',
              data: this.canceledOrdersDatasetData,
              cubicInterpolationMode: 'monotone',
              pointRadius: 2,
              pointHoverRadius: 8,
            },
          ],
        }
      },
      acceptedOrdersDatasetData () {
        return this.items.map((item) => item.acceptedOrdersCount)
      },
      canceledOrdersDatasetData () {
        return this.items.map((item) => item.canceledOrdersCount)
      },
      datasetLabel () {
        if (!this.items.length) {
          return ''
        }

        if (this.items.length === 1) {
          return new Date(this.items[0].date).toLocaleDateString('ru', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
          })
        }

        const startDate = new Date(this.items[0].date).toLocaleDateString('ru', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
        })

        const endDate = new Date(
          this.items[this.items.length - 1].date,
        ).toLocaleDateString('ru', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
        })

        return startDate + ' - ' + endDate
      },
    },
    methods: {
      externalTooltipHandler (context) {
        const { chart, tooltip } = context
        const tooltipEl = createTooltip(chart)

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        if (!tooltip.dataPoints.length) {
          return
        }

        // Remove old children
        while (tooltipEl.firstChild) {
          tooltipEl.firstChild.remove()
        }

        const data = this.items[tooltip.dataPoints[0].dataIndex]
        const isCanceledOrders = !!tooltip.dataPoints[0].datasetIndex

        tooltipEl.appendChild(this.createLeftPart(data, isCanceledOrders))
        tooltipEl.appendChild(this.createRightPart(data, isCanceledOrders))

        positioningTooltip(chart, tooltip, tooltipEl)
      },
      createLeftPart (data, isCanceledOrders = false) {
        const part = document.createElement('div')
        part.style.padding = '5px 10px'
        part.style.borderRight = '2px solid #7E7E7E'

        const acceptedOrders = document.createElement('div')
        acceptedOrders.style.color = isCanceledOrders ? '#EA306B' : '#FF9D00'
        acceptedOrders.style.fontSize = '18px'

        const count = isCanceledOrders
          ? data.canceledOrdersCount
          : data.acceptedOrdersCount

        acceptedOrders.appendChild(document.createTextNode(count + ' зак.'))

        part.appendChild(this.createDateBlock(data.date))
        part.appendChild(acceptedOrders)

        return part
      },
      createDateBlock (date) {
        const dateBlock = document.createElement('div')
        dateBlock.style.color = '#D3D5D9'
        dateBlock.style.fontSize = '14px'
        dateBlock.style.lineHeight = '16px'

        const formattedWeekDay = new Date(date).toLocaleString('ru', {
          weekday: 'short',
        })
        const formattedDate = new Date(date).toLocaleString('ru', {
          day: 'numeric',
          month: 'short',
        })

        dateBlock.appendChild(
          document.createTextNode(
            `${formattedWeekDay.toUpperCase()} ${formattedDate}`,
          ),
        )

        return dateBlock
      },
      createRightPart (data, isCanceledOrders = false) {
        const part = document.createElement('div')
        part.style.paddingRight = '10px'
        part.style.paddingLeft = '10px'

        const label = document.createElement('div')
        label.style.color = '#949191'
        label.appendChild(
          document.createTextNode(isCanceledOrders ? '% отказа' : 'СВП'),
        )

        part.appendChild(label)
        part.appendChild(
          isCanceledOrders
            ? this.createCancelPercentBlock(data.cancelPercent)
            : createSVPBlock(data.avgConfirmTime),
        )

        return part
      },
      createCancelPercentBlock (cancelPercent) {
        const block = document.createElement('div')
        block.style.color = '#EA306B'
        block.style.fontSize = '16px'

        block.appendChild(document.createTextNode(`${cancelPercent}%`))

        return block
      },
    },
  }
</script>

<style lang="less" scoped>
@import 'src/assets/styles/statistic.less';
</style>
